// src/lib/plytixDataLoader.js
import { productStore } from './productStore.js';

// Track page speed timing from navigation start
let pageSpeedTrackingEnabled = false;
let navigationStartTime = null;
let domContentLoadedTime = null;
let isInitialized = false; // Guard to prevent multiple initializations
let dataLoaded = false; // Guard to prevent multiple data loads

/**
 * Initialize page speed tracking if enabled
 */
function initPageSpeedTracking() {
  try {
    const jsonElement = document.getElementById('product-families-json-data');
    pageSpeedTrackingEnabled = jsonElement?.dataset.speedTracking === 'true';
    
    if (pageSpeedTrackingEnabled) {
      // Get navigation timing
      const navigationTiming = performance.getEntriesByType('navigation')[0];
      if (navigationTiming) {
        // Use navigation start as baseline (0ms)
        navigationStartTime = 0;
        
        // Track DOMContentLoaded
        if (document.readyState !== 'loading') {
          // DOM already loaded, calculate time from navigation
          domContentLoadedTime = navigationTiming.domContentLoadedEventEnd || 0;
        } else {
          document.addEventListener('DOMContentLoaded', () => {
            const navTiming = performance.getEntriesByType('navigation')[0];
            domContentLoadedTime = navTiming?.domContentLoadedEventEnd || performance.now();
          });
        }
        
        // Log initial page metrics
        const domInteractive = navigationTiming.domInteractive || 0;
        const domContentLoaded = navigationTiming.domContentLoadedEventEnd || 0;
        const loadEvent = navigationTiming.loadEventEnd || 0;
        const responseStart = navigationTiming.responseStart || 0;
        const responseEnd = navigationTiming.responseEnd || 0;
        
        console.log('[Plytix Speed] 📊 Page Performance Metrics:');
        console.log(`[Plytix Speed] Response Start: ${responseStart.toFixed(2)}ms`);
        console.log(`[Plytix Speed] Response End: ${responseEnd.toFixed(2)}ms`);
        if (domInteractive > 0) {
          console.log(`[Plytix Speed] DOM Interactive: ${domInteractive.toFixed(2)}ms`);
        }
        if (domContentLoaded > 0) {
          console.log(`[Plytix Speed] DOMContentLoaded: ${domContentLoaded.toFixed(2)}ms`);
        }
        if (loadEvent > 0) {
          console.log(`[Plytix Speed] Load Event: ${loadEvent.toFixed(2)}ms`);
        }
      }
    }
  } catch (error) {
    console.error('Error initializing page speed tracking:', error);
  }
}

/**
 * Get page speed metrics relative to navigation start
 */
function getPageSpeedMetrics(endTime) {
  if (!pageSpeedTrackingEnabled || !endTime) {
    return null;
  }
  
  try {
    const navigationTiming = performance.getEntriesByType('navigation')[0];
    if (!navigationTiming) {
      return null;
    }
    
    // Calculate time from navigation start (navigation timing uses relative timestamps)
    // endTime is from performance.now() which is relative to navigation start
    const fromNavigationStart = endTime;
    const fromDOMContentLoaded = navigationTiming.domContentLoadedEventEnd > 0 
      ? endTime - navigationTiming.domContentLoadedEventEnd 
      : null;
    const fromPageLoad = navigationTiming.loadEventEnd > 0
      ? endTime - navigationTiming.loadEventEnd
      : null;
    
    const metrics = {
      fromNavigationStart,
      fromDOMContentLoaded,
      fromPageLoad,
      domInteractive: navigationTiming.domInteractive || 0,
      domContentLoaded: navigationTiming.domContentLoadedEventEnd || 0,
      loadEvent: navigationTiming.loadEventEnd || 0,
      responseStart: navigationTiming.responseStart || 0,
      responseEnd: navigationTiming.responseEnd || 0
    };
    
    return metrics;
  } catch (error) {
    return null;
  }
}

/**
 * Load and process Plytix product data from Liquid-generated JSON
 */
function loadPlytixProductData() {
  // Prevent multiple calls
  if (dataLoaded) {
    return;
  }
  
  const jsonElement = document.getElementById('product-families-json-data');
  const speedTrackingEnabled = jsonElement?.dataset.speedTracking === 'true';
  
  if (!jsonElement) {
    if (speedTrackingEnabled) {
      console.log('[Plytix Speed] JSON element not found - Plytix data not enabled');
    }
    return;
  }
  
  // Mark as loaded to prevent duplicates
  dataLoaded = true;

  const startTime = speedTrackingEnabled ? performance.now() : null;

  try {
    const parseStartTime = speedTrackingEnabled ? performance.now() : null;
    const plytixProductData = JSON.parse(jsonElement.textContent);
    const parseTime = speedTrackingEnabled && parseStartTime !== null ? performance.now() - parseStartTime : null;
    
    if (speedTrackingEnabled && parseTime !== null) {
      console.log(`[Plytix Speed] JSON Parse: ${parseTime.toFixed(2)}ms`);
      console.log(`[Plytix Speed] Products in JSON: ${plytixProductData?.length || 0}`);
    }
    
    if (plytixProductData && Array.isArray(plytixProductData) && plytixProductData.length > 0) {
      const firstProductType = plytixProductData[0]?.productType;
      if (firstProductType && firstProductType.trim().toLowerCase() === 'bundles') {
        console.warn('[Plytix] Plytix data is enabled but not used — bundle product type detected');
        return;
      }

      const processStartTime = speedTrackingEnabled ? performance.now() : null;
      const processedData = productStore.processProductFamiliesData(plytixProductData);
      const processTime = speedTrackingEnabled && processStartTime !== null ? performance.now() - processStartTime : null;
      
      // Store globally for access by other components
      window.productFamiliesData = processedData;
      // Mark that data came from Plytix metafield (Liquid-generated)
      window.productFamiliesDataSource = 'plytix-metafield';
      
      if (speedTrackingEnabled && processTime !== null) {
        console.log(`[Plytix Speed] Data Processing: ${processTime.toFixed(2)}ms`);
        console.log(`[Plytix Speed] Products Processed: ${processedData.length}`);
        console.log(`[Plytix Speed] 📍 Data Source: Plytix PDP Stitch Products Metafield (Liquid-generated JSON)`);
      }
      
      // Emit event that data is ready (similar to product-data-updated)
      // Emitter is passed as parameter to avoid circular dependency
      if (window.emitter) {
        window.emitter.emit('plytix-product-data-ready', processedData);
      }
      
      const totalTime = speedTrackingEnabled && startTime !== null ? performance.now() - startTime : null;
      const completionTime = speedTrackingEnabled ? performance.now() : null;
      
      if (speedTrackingEnabled && totalTime !== null) {
        console.log(`[Plytix Speed] ✅ Total Plytix Processing Time: ${totalTime.toFixed(2)}ms`);
        console.log(`[Plytix Speed] Breakdown: Parse ${parseTime?.toFixed(2)}ms + Process ${processTime?.toFixed(2)}ms`);
        
        // Log page speed metrics
        if (completionTime) {
          const pageMetrics = getPageSpeedMetrics(completionTime);
          if (pageMetrics) {
            console.log(`[Plytix Speed] 📊 Total Page Speed Metrics:`);
            console.log(`[Plytix Speed] Time from Navigation Start: ${pageMetrics.fromNavigationStart.toFixed(2)}ms`);
            if (pageMetrics.fromDOMContentLoaded !== null) {
              console.log(`[Plytix Speed] Time from DOMContentLoaded: ${pageMetrics.fromDOMContentLoaded.toFixed(2)}ms`);
            }
            if (pageMetrics.fromPageLoad !== null) {
              console.log(`[Plytix Speed] Time from Page Load: ${pageMetrics.fromPageLoad.toFixed(2)}ms`);
            }
            console.log(`[Plytix Speed] ─────────────────────────────────────────`);
            console.log(`[Plytix Speed] Plytix completed at: ${completionTime.toFixed(2)}ms from page start`);
          }
        }
      }
      
    } else {
      if (speedTrackingEnabled && startTime !== null) {
        const emptyTime = performance.now() - startTime;
        console.log(`[Plytix Speed] ⚠️ No data to process: ${emptyTime.toFixed(2)}ms (JSON exists but empty)`);
      }
    }
  } catch (error) {
    console.error('Error parsing Plytix product data JSON:', error);
    if (speedTrackingEnabled && startTime !== null) {
      const errorTime = performance.now() - startTime;
      console.log(`[Plytix Speed] ❌ Parse Error after ${errorTime.toFixed(2)}ms`);
    }
  }
}

/**
 * Initialize Plytix data loader if enabled
 * @param {Object} emitterInstance - The emitter instance from main.js
 */
export function initPlytixDataLoader(emitterInstance) {
  // Store emitter on window for access within the module
  if (emitterInstance) {
    window.emitter = emitterInstance;
  }
  
  // Prevent multiple initializations
  if (isInitialized) {
    return;
  }
  isInitialized = true;
  
  // Check if Plytix data is enabled (JSON element exists)
  // Use a function to check both immediately and after DOM load
  function checkAndInit() {
    // Double-check guard in case this is called multiple times
    if (dataLoaded) {
      return true;
    }
    
    const jsonElement = document.getElementById('product-families-json-data');
    if (!jsonElement) {
      return false; // Plytix data not enabled
    }

    // Initialize page speed tracking immediately (only once)
    if (!pageSpeedTrackingEnabled) {
      initPageSpeedTracking();
    }

    // Load data when DOM is ready
    if (document.readyState === 'loading') {
      // Only add listener if not already added
      if (!document.hasAttribute('data-plytix-listener-added')) {
        document.setAttribute('data-plytix-listener-added', 'true');
        document.addEventListener('DOMContentLoaded', loadPlytixProductData);
      }
    } else {
      loadPlytixProductData();
    }
    
    return true;
  }
  
  // Try immediately
  if (!checkAndInit()) {
    // If element doesn't exist yet, wait for DOM (only once)
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', checkAndInit);
    }
  }
}
